import { isPlainObject, PlainObject } from '../helpers/helpers';
import { markBlockAddedSinceBuild } from '../helpers/addedBlocks';
import { BuildOnlyBlocks } from '../loading/loadBuildOnlyBlocks';
import { FieldNameConverter } from './fieldNames';

const getBlockEntryUid = (blockContent: unknown): string | undefined => {
  if (!isPlainObject(blockContent)) return undefined;

  const { item } = blockContent;
  const entry = Array.isArray(item) ? item[0] : item;

  return isPlainObject(entry) ? entry.uid : undefined;
};

export const createEntryMerger = (
  { toFieldName, metadataFieldName }: FieldNameConverter,
  bodyBlockTypeNames: Record<string, string>
) => {
  const typeNameByBlockType: Record<string, string> = Object.fromEntries(
    Object.entries(bodyBlockTypeNames).map(([apiBlockType, typeName]) => [
      toFieldName(apiBlockType),
      typeName,
    ])
  );

  const getModularBlockType = (value: PlainObject): string | undefined => {
    const fieldNames = Object.keys(value);
    const [onlyFieldName] = fieldNames;

    return fieldNames.length === 1 &&
      isPlainObject(value[onlyFieldName]) &&
      metadataFieldName in value[onlyFieldName]
      ? onlyFieldName
      : undefined;
  };

  const isDifferentItem = (
    currentValue: PlainObject,
    previewValue: PlainObject
  ): boolean => {
    const isDifferentEntryOrAsset =
      typeof currentValue.uid === 'string' &&
      typeof previewValue.uid === 'string' &&
      currentValue.uid !== previewValue.uid;

    const isDifferentFile =
      typeof previewValue.filename === 'string' &&
      typeof currentValue.url === 'string' &&
      currentValue.url !== previewValue.url;

    const previewBlockType = getModularBlockType(previewValue);
    const isDifferentBlockType =
      !!previewBlockType && !currentValue[previewBlockType];

    return isDifferentEntryOrAsset || isDifferentFile || isDifferentBlockType;
  };

  const mergeWithBuildData = (
    currentValue: unknown,
    previewValue: unknown
  ): unknown => {
    if (previewValue === undefined) return currentValue;

    if (Array.isArray(currentValue) && Array.isArray(previewValue)) {
      return previewValue.map((previewItem: unknown, index) =>
        index < currentValue.length
          ? mergeWithBuildData(currentValue[index], previewItem)
          : previewItem
      );
    }

    if (isPlainObject(currentValue) && isPlainObject(previewValue)) {
      if (isDifferentItem(currentValue, previewValue)) return previewValue;

      const merged: PlainObject = { ...currentValue };

      Object.keys(previewValue).forEach((fieldName) => {
        merged[fieldName] =
          fieldName in currentValue
            ? mergeWithBuildData(
                currentValue[fieldName],
                previewValue[fieldName]
              )
            : previewValue[fieldName];
      });

      return merged;
    }

    return previewValue;
  };

  const getBodyBlockType = (block: PlainObject): string | undefined => {
    const filledFieldNames = Object.keys(block).filter(
      (fieldName) => block[fieldName] && fieldName !== metadataFieldName
    );

    return (
      filledFieldNames.find((fieldName) => fieldName in typeNameByBlockType) ??
      filledFieldNames[0]
    );
  };

  const mergeBody = (
    currentBody: PlainObject[],
    previewBody: PlainObject[],
    buildOnlyBlocks?: BuildOnlyBlocks
  ): PlainObject[] => {
    const matchedBlockIndexes = new Set<number>();

    const isUnmatchedBlockOfType =
      (blockType: string) => (block: PlainObject, index: number) =>
        !matchedBlockIndexes.has(index) &&
        getBodyBlockType(block) === blockType;

    const findMatchingBlockIndex = (
      blockType: string,
      entryUid?: string
    ): number => {
      const isCandidate = isUnmatchedBlockOfType(blockType);

      if (entryUid) {
        const sameEntryIndex = currentBody.findIndex(
          (block, index) =>
            isCandidate(block, index) &&
            getBlockEntryUid(block[blockType]) === entryUid
        );

        if (sameEntryIndex !== -1) return sameEntryIndex;

        const hasCandidatesWithOtherEntries = currentBody.some(
          (block, index) =>
            isCandidate(block, index) && getBlockEntryUid(block[blockType])
        );

        if (hasCandidatesWithOtherEntries) return -1;
      }

      return currentBody.findIndex(isCandidate);
    };

    return previewBody.map((previewBlock) => {
      const blockType = getBodyBlockType(previewBlock);

      if (!blockType) return previewBlock;

      const matchingIndex = findMatchingBlockIndex(
        blockType,
        getBlockEntryUid(previewBlock[blockType])
      );

      if (matchingIndex !== -1) {
        matchedBlockIndexes.add(matchingIndex);

        return mergeWithBuildData(
          currentBody[matchingIndex],
          previewBlock
        ) as PlainObject;
      }

      const entryUid = getBlockEntryUid(previewBlock[blockType]);
      const buildOnlyBlock = entryUid
        ? buildOnlyBlocks?.[blockType]?.[entryUid]
        : undefined;

      if (buildOnlyBlock) {
        return mergeWithBuildData(
          {
            [blockType]: {
              typeName: typeNameByBlockType[blockType],
              item: [buildOnlyBlock],
            },
          },
          previewBlock
        ) as PlainObject;
      }

      const { [blockType]: blockContent, ...otherFields } = previewBlock;

      return {
        [blockType]: markBlockAddedSinceBuild({
          typeName: typeNameByBlockType[blockType],
          ...blockContent,
        }),
        ...otherFields,
      };
    });
  };

  const isNonEmptyBlockList = (value: unknown): value is PlainObject[] =>
    Array.isArray(value) && value.length > 0 && value.every(isPlainObject);

  const mergeEntry = (
    currentEntry: PlainObject,
    previewEntry: PlainObject,
    buildOnlyBlocks?: BuildOnlyBlocks
  ): PlainObject => {
    const merged = mergeWithBuildData(
      currentEntry,
      previewEntry
    ) as PlainObject;

    if (
      Array.isArray(currentEntry.body) &&
      isNonEmptyBlockList(previewEntry.body)
    ) {
      merged.body = mergeBody(
        currentEntry.body,
        previewEntry.body,
        buildOnlyBlocks
      );
    }

    return merged;
  };

  return { mergeEntry };
};
